import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useImageUploadToS3 } from 'shared/hooks/PetsCars/useImageUploadToS3';
import { FilesTobeSavedProps } from 'shared/interfaces/IModalForm';
import { IUpload } from 'shared/interfaces/IUpload';
import { setUser } from 'shared/redux/slicers/user.slicer';
import { useAppSelector } from 'shared/redux/store';
import { talentService } from 'shared/services/talentService';
import { v4 as uuidv4 } from 'uuid';

type UseSubmitForm<T, S> = {
  data: T[];
  handleSavingInfo?: (data: S) => void;
  type: 'cars' | 'pets' | 'asset_props';
};

export type ProgressProps = 'saving' | 'editing' | 'deleting' | 'idle';

export type HandleAddItemProps<T> = {
  filesToBeSaved: FilesTobeSavedProps[];
  items: T;
};

export type HandleEditItemProps<T> = {
  filesToBeSaved: FilesTobeSavedProps[];
  items: T;
  id: string;
};

export type ItemWithID = {
  id: string;
};

const { updateMe } = talentService();

export const useSubmitForm = <T, S>({ data, handleSavingInfo, type }: UseSubmitForm<T, S>) => {
  const dispatch = useDispatch();
  const { user: userState } = useAppSelector((state) => state);
  const { mutate: updateMutate } = updateMe();
  const { uploadImageToS3 } = useImageUploadToS3();
  const [progress, setProgress] = useState<ProgressProps>('idle');
  const handleAddItem = async <T>({ filesToBeSaved, items }: HandleAddItemProps<T>) => {
    setProgress('saving');
    const newItem = {
      id: uuidv4(),
      ...items,
    };
    let attached_media: IUpload[] = [];

    if (handleSavingInfo) handleSavingInfo(newItem as S);
    if (filesToBeSaved && filesToBeSaved.length > 0) {
      attached_media = await uploadImageToS3({ filesToBeSaved });
    }
    updateMutate(
      {
        [type]: [...data, { ...newItem, attached_media }],
      },
      {
        onSuccess: async () => {
          dispatch(
            setUser({
              user: {
                ...userState.user,
                attributes: {
                  ...userState.user?.attributes,
                  [type]: [...data, { ...newItem, attached_media }],
                },
              },
            }),
          );
          setProgress('idle');
        },
      },
    );
  };

  const handleEditItem = async <P>({ filesToBeSaved, id, items }: HandleEditItemProps<P>) => {
    setProgress('editing');
    const editedCar = {
      id,
      ...items,
    };
    // Get the current List
    const currentItemList: T[] = data || [];

    // If theres an new image upload
    let attached_media: IUpload[] = [];

    // Use the filesToBeSaved to upload the new images to S3, And noted that it needs to slice base on the Slice Counter
    // Current Image are images that is already uploaded. The "filesToBeSaved" is combined with the new files and the current uploaded files
    // We use this to display the current image and the new image from the ImageUploadedPlaceHolder.tsx
    // The slice prevents checking the Current Image Uploaded and we only need to upload the new uploaded images
    if (filesToBeSaved && filesToBeSaved?.length > 0) {
      attached_media = await uploadImageToS3({ filesToBeSaved });
    }
    // Modify the carList and the selected car
    const modifiedItemList = currentItemList?.map((item) => {
      if ((item as ItemWithID)?.id === id) {
        return {
          ...editedCar,
          // Spread the current Images first before the new files uploaded
          attached_media,
        };
      } else {
        return item;
      }
    });
    updateMutate(
      {
        [type]: modifiedItemList,
      },
      {
        onSuccess: async () => {
          dispatch(
            setUser({
              user: {
                ...userState.user,
                attributes: {
                  ...userState.user?.attributes,
                  [type]: modifiedItemList,
                },
              },
            }),
          );
          setProgress('idle');
        },
      },
    );
  };

  const handleDeleteItem = async ({ id }: { id: string }) => {
    setProgress('deleting');
    // Get the current list of the cars
    const currenCarList = data || [];
    // Modified the current list by deleting the selected ID
    const modifiedItemList = currenCarList?.filter((item) => {
      if ((item as ItemWithID)?.id === id) {
        return false;
      } else {
        return true;
      }
    });
    // Use the modifield list to mutate the cars list
    updateMutate(
      {
        [type]: modifiedItemList,
      },
      {
        onSuccess: async () => {
          dispatch(
            setUser({
              user: {
                ...userState.user,
                attributes: {
                  ...userState.user?.attributes,
                  [type]: modifiedItemList,
                },
              },
            }),
          );
          setProgress('idle');
        },
      },
    );
  };

  return { handleAddItem, handleEditItem, handleDeleteItem, progress };
};
